var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import Link from '@rad/components/utility/link';
import ActionButton from '@rad/components/ui/action-button';
import ActionMenu from '@rad/components/ui/action-menu';
import { useFetch, usePromise, useRouting } from '@rad/hooks';
import useAuth from '../../../../hooks/useAuth';
import { contribute, contributeAtAddress } from '../../../../common/network/property';
import { contributeAsOrg, contributeAsOrgAtAddress } from '../../../../common/network/org-story';
import { myOrganizations } from '../../../../common/network/community';
import { generalRoutes, accountRoutes, propertyRoutes, onSubdomain } from '../../../../common/routes';
import { exploreURL } from '../../../../common/explore-filters';
import { Permission } from '../../../../common/routes/types';
import { useCommunity } from '../../../../hooks/useDistrict';
import { DEFAULT_PARCEL_KIND } from '../../../../types/parcel-kind';
const VARIANTS = {
    'hero': {
        'button': 'btn btn-light fw-semibold align-self-start mt-2',
        'menu': 'align-self-start mt-2',
        'menuVariant': 'light',
        'menuSize': 'md',
        'trigger': 'fw-semibold',
        'label': 'Contribute',
        'loginLabel': 'Login to Contribute',
    },
    'popup': {
        'button': 'btn btn-primary btn-sm w-100',
        'menu': 'w-100',
        'menuVariant': 'primary',
        'menuSize': 'sm',
        'trigger': 'w-100',
        'label': 'Start contributing',
        'loginLabel': 'Log in to start contributing',
    },
};
// The parcel the server registers for an address it does not hold yet.
const addressParcel = (target) => ({ 'point': target.point, 'address': target.address, 'kind': DEFAULT_PARCEL_KIND });
// One request either way: the server finds or registers an address's parcel in the same transaction.
const contributeTo = (target) => target.kind === 'parcel'
    ? contribute(target.id)
    : contributeAtAddress(addressParcel(target));
const contributeAsOrgTo = (communityId, target) => target.kind === 'parcel'
    ? contributeAsOrg(communityId, target.id)
    : contributeAsOrgAtAddress(communityId, addressParcel(target));
// Contribute as yourself or, given an organization that lets you, as it; the editor opens on that view.
// One choice is a plain button; several make the button itself the menu.
const SignedInContribute = (props) => {
    var _a, _b;
    const [, navigate] = useRouting();
    const [auth] = useAuth();
    const community = useCommunity();
    const [organizationsP] = useFetch(myOrganizations);
    const organizations = ((_a = usePromise(organizationsP)) !== null && _a !== void 0 ? _a : []).filter(org => org.canTellStories);
    // On a district's own host its staff may contribute as the district.
    const district = onSubdomain && community.district.kind === 'district' && auth.hasPermission([Permission.StoriesCreate, Permission.SystemDistricts])
        ? community.district
        : null;
    const [busy, setBusy] = useState(false);
    const style = VARIANTS[(_b = props.variant) !== null && _b !== void 0 ? _b : 'hero'];
    const open = (propertyId) => navigate(accountRoutes.myPropertiesManage.getURL({ 'id': propertyId }));
    const asSelf = () => __awaiter(void 0, void 0, void 0, function* () {
        const result = yield contributeTo(props.target);
        return open(result.propertyId);
    });
    const asCommunity = (communityId) => __awaiter(void 0, void 0, void 0, function* () {
        const result = yield contributeAsOrgTo(communityId, props.target);
        return open(result.propertyId);
    });
    // Menu items cannot show their own progress, so the trigger does.
    const run = (action) => __awaiter(void 0, void 0, void 0, function* () {
        setBusy(true);
        try {
            yield action();
        }
        finally {
            setBusy(false);
        }
    });
    if (organizations.length === 0 && district === null) {
        return (_jsxs(ActionButton, { className: style.button, loadingText: "Opening\u2026", onClick: asSelf, children: [_jsx("i", { className: "bi bi-pencil-square me-2" }), style.label] }));
    }
    return _jsx(_Fragment, { children: _jsxs(ActionMenu, { className: style.menu, variant: style.menuVariant, size: style.menuSize, triggerClassName: style.trigger, disabled: busy, label: _jsxs(_Fragment, { children: [_jsx("i", { className: "bi bi-pencil-square me-2" }), busy ? 'Opening…' : style.label] }), children: [_jsxs(ActionMenu.Item, { onClick: () => run(asSelf), children: [_jsx("i", { className: "bi bi-person me-2" }), "As yourself"] }), organizations.map(org => (_jsxs(ActionMenu.Item, { onClick: () => run(() => asCommunity(org.id)), children: [_jsx("i", { className: "bi bi-building me-2" }), "As ", org.title] }, org.id))), district && (_jsxs(ActionMenu.Item, { onClick: () => run(() => asCommunity(district.id)), children: [_jsx("i", { className: "bi bi-buildings me-2" }), "As ", district.title] }))] }) });
};
// Opens this visitor's own view of a property and takes them to the editor for it. A view is one
// contributor's take on a property, not a claim to own it, and adding to the public record is
// never gated — anyone signed in may open one, however many they already hold.
//
// A signed-out visitor goes through login and lands back here to press it again. The editing
// screens never render this — a contributor already editing has the form in front of them.
const ContributeButton = (props) => {
    var _a;
    const [auth] = useAuth();
    const style = VARIANTS[(_a = props.variant) !== null && _a !== void 0 ? _a : 'hero'];
    // Where login sends the visitor back to: an address to Explore centered on it, a parcel to its page.
    const returnUrl = props.target.kind === 'address'
        ? exploreURL({ 'query': props.target.address, 'center': props.target.point })
        : propertyRoutes.parcel.getURL({ 'id': props.target.id });
    return auth.isAuthenticated
        ? _jsx(SignedInContribute, { target: props.target, variant: props.variant })
        : _jsxs(Link, { href: `${generalRoutes.login.getURL()}?next=${encodeURIComponent(returnUrl)}`, className: style.button, children: [_jsx("i", { className: "bi bi-pencil-square me-2" }), style.loginLabel] });
};
export default ContributeButton;
